import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../../Hooks/useNavigateAndScroll';

const FunctionalMedicine = () => {

  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();
  const infoCardsData = {
    expectantParents: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/expectant_parents.webp",
      infoTitle: "Expectant Parents",
      infoText: "Comprehensive prenatal care, guidance, and support for expecting mothers and families preparing for their new arrival.",
      path: "/services/expectant-parents"
    },
    wellChecks: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Well+Check.webp",
      infoTitle: "Well Checks", 
      infoText: "Regular preventive care appointments to monitor your child's growth, development, and overall health at every stage.",
      path: "/services/well-checks"
    },
    sameDaySickVisits: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/sick+same+day.webp",
      infoTitle: "Same Day Sick Visits",
      infoText: "Prompt medical attention for acute illnesses and urgent health concerns when your child needs immediate care.",
      path: "/services/same-day-sick-visits"
    },
    functionalMedicine: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Functional+Medicine.webp",
      infoTitle: "Functional Medicine",
      infoText: "Holistic approach to healthcare focusing on identifying and addressing the root causes of health issues for optimal wellness.",
      path: "/services/functional-medicine"
    },
    commonMedicalConditions: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/common+medical+conditions.webp",
      infoTitle: "Common Medical Conditions",
      infoText: "Information and treatment options for frequently encountered pediatric and family health conditions and concerns.",
      path: "/services/common-medical-conditions"
    }
  };

  return (
    <div>
    <div className="relative w-auto z-1 mt-[-70px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-forms.s3.us-east-2.amazonaws.com/functional+medicine+(1).webp"
            alt="Mother and child meeting a doctor"
          />
        </div>
    <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-50">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>
    <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
      <h2 className="text-4xl font-bold text-gray-800 mb-2">Functional Medicine: A Guide for Parents</h2>
      <hr className="border-t-2 border-blue-500 mb-6" />

      <div className="space-y-6">
        <section>
          <p className="mb-4 text-gray-600">
            Your body operates as a unified system, not a collection of isolated parts. Every function in your body is interconnected, working together to support your overall well-being. This holistic perspective lies at the heart of functional medicine.
          </p>
        </section>

        <section>
          <h3 className="text-3xl font-bold text-gray-800 mb-4">What Is Functional Medicine?</h3>
          <p className="mb-4 text-gray-600">
            Functional medicine is an innovative, patient-centered approach to healthcare that focuses on treating the whole person rather than just managing symptoms. It seeks to uncover and address the root causes of health concerns by considering genetic, environmental, and lifestyle factors unique to each individual.
          </p>
        </section>

        <section>
          <h3 className="text-3xl font-bold text-gray-800 mb-4">Core Principles of Functional Medicine</h3>
          <p className="mb-4 text-gray-600">
            Guided by six foundational principles, functional medicine emphasizes a personalized and integrative approach to health. According to the Institute for Functional Medicine, these principles include:
          </p>
          <ul className="list-disc list-outside ml-5 text-gray-600 space-y-2">
            <li>Recognizing the biochemical individuality of each person, shaped by genetic and environmental factors.</li>
            <li>Prioritizing a patient-centered approach over a disease-focused model.</li>
            <li>Striving for balance within the body, mind, and spirit.</li>
            <li>Understanding the interconnected nature of internal physiological systems.</li>
            <li>Defining health as a state of positive vitality, not merely the absence of disease.</li>
            <li>Enhancing organ reserve to improve health span and overall vitality, not just lifespan.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-3xl font-bold text-gray-800 mb-4">Benefits of Functional Medicine</h3>
          <p className="mb-4 text-gray-600">
            Functional medicine combines elements of Western and Eastern medicine, alternative therapies, and evidence-based diagnostic methods to deliver comprehensive care. This integrative approach ensures a thorough understanding of your health and provides effective solutions tailored to your unique needs.
          </p>
        </section>

        <section>
          <h3 className="text-3xl font-bold text-gray-800 mb-4">How Functional Medicine Differs from Conventional Care</h3>
          <p className="mb-4 text-gray-600">
            Patients often notice immediate differences when transitioning to functional medicine:
          </p>
          <ul className="list-disc list-outside ml-5 text-gray-600 space-y-2 mb-4">
            <li><span className="font-bold">Personalized Care:</span> More time with your healthcare provider to discuss your health history, concerns, and goals.</li>
            <li><span className="font-bold">Holistic Focus:</span> Attention to environmental, genetic, and lifestyle factors impacting your health.</li>
            <li><span className="font-bold">Preventative Strategies:</span> Guidance on lifestyle modifications to enhance well-being and prevent illness.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-3xl font-bold text-gray-800 mb-4">Functional Medicine for Children</h3>
          <p className="mb-4 text-gray-600">
            Functional medicine can be particularly beneficial for children by addressing underlying health concerns and promoting long-term wellness. If you're seeking a comprehensive and personalized approach to improve your child’s health, our pediatric team at Central Illinois Pediatric Associates is here to help.
          </p>
          <p className="mb-4 text-gray-600">
            To learn more or schedule an appointment, call our office today at{' '}
            <a href="tel:+13096631011" className="text-blue-500 hover:underline">(309) 663-1011</a>. Let us partner with you to optimize your child’s health and well-being!
          </p>
        </section>
      </div>
    </div>
    </div>
  );
};

export default FunctionalMedicine;
